
import React, { Component } from 'react';
import { HTTPService } from "./../services/httpService";
import JOI from 'joi-browser';

require('dotenv').config();

const API = new HTTPService();

export default class Users extends Component {

    constructor(props) {
        super(props);
        this._isMounted = true;
    }

    state = { 
        // View mode: 'list', 'create', 'edit'
        viewMode: 'list',
        
        // User data for create/edit forms
        data: {
            email: "",
            name: "",
            phone: "",
            user_type: "",
            access: "",
            id: "",
            primary_company_id: "",
            company_type: ""
        },
        errors: {},
        
        // Lists
        userList: [],
        dealerList: [],
        specialistList: [],
        availableCompanies: [],
        
        // Multi-company associations
        userCompanies: [], // Additional companies (excluding primary)
        allUserCompanies: [], // All companies user has access to (from API)
        
        // Search and filtering
        searchTerm: "",
        filteredUsers: [],
        
        // Access levels for dealers
        dealerAccessLevels: [
            { value: "ASSIST", label: "Assist" },
            { value: "DIRECT", label: "Direct" },
            { value: "STANDARD", label: "Standard" }
        ],
        
        // Cognito status
        cognitoAccountStatus: null,
        isCheckingCognito: false
    };

    async componentDidMount() {            
        await this.loadUsers();
        await this.loadCompanies();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    // Load all users for the list view
    async loadUsers() {
        try {
            const userRequest = "/users";
            const { data: users } = await API.get(userRequest);
            this.setState({ 
                userList: users,
                filteredUsers: users 
            });
            //console.log("loadUsers: User List ", users);
        } catch (error) {
            console.error("Error loading users:", error);
        }
    }

    // Load available companies
    async loadCompanies() {
        try {
            // Load dealerships
            const dealerRequest = "/dealers";
            const { data: dealers } = await API.get(dealerRequest);
            dealers.forEach(function (element) {
                element.company_type = "dealership";
            });
            
            // Load specialists
            const specialistRequest = "/installers";
            const { data: specialists } = await API.get(specialistRequest);
            specialists.forEach(function (element) {
                element.company_type = "specialist";
            });
            
            const allCompanies = [...dealers, ...specialists];
            
            this.setState({ 
                dealerList: dealers,
                specialistList: specialists,
                availableCompanies: allCompanies 
            });
        } catch (error) {
            console.error("Error loading companies:", error);
        }
    }

    // Load user's company associations
    async loadUserCompanies(userId) {
        try {
            const userCompaniesRequest = "/useraccess/" + userId;
            const { data: userCompanies } = await API.get(userCompaniesRequest);
            //console.log("Raw user companies from /useraccess:", userCompanies);
            
            // Process the company data
            let processedCompanies = [];
            
            if (Array.isArray(userCompanies)) {
                processedCompanies = userCompanies.map(uc => {
                    // Handle if it's already in the right format
                    if (uc.company_id && uc.company_type) {
                        return uc;
                    }
                    
                    // Handle if it's direct company data (Installer_id, Installer_Name, etc.)
                    if (uc.Installer_id) {
                        return {
                            company_id: uc.Installer_id,
                            company_type: "specialist",
                            company_name: uc.Installer_Name
                        };
                    }
                    
                    if (uc.dealer_id) {
                        return {
                            company_id: uc.dealer_id,
                            company_type: "dealership", 
                            company_name: uc.dealer_name
                        };
                    }
                    
                    return null;
                }).filter(Boolean);
            }
            
            //console.log("Processed user companies:", processedCompanies);
            
            // Filter to only show companies of the same type as the user (excluding primary)
            const filteredCompanies = processedCompanies.filter(uc => 
                uc.company_type === this.state.data.company_type &&
                uc.company_id !== this.state.data.primary_company_id
            );
            
            //console.log("Filtered additional companies:", filteredCompanies);
            
            // Store all companies (including primary) for getUserAccessibleCompanies
            this.setState({ 
                userCompanies: filteredCompanies,
                allUserCompanies: processedCompanies
            });
            
        } catch (error) {
            console.error("Error loading user companies:", error);
            this.setState({ 
                userCompanies: [],
                allUserCompanies: [] 
            });
        }
    }

    // Check Cognito status
    async checkCognitoStatus(email) {
        try {
            this.setState({ isCheckingCognito: true });
            const statusRequest = "/auth/user-status/" + encodeURIComponent(email);
            const { data: statusResult } = await API.get(statusRequest);
            this.setState({ 
                isCheckingCognito: false, 
                cognitoAccountStatus: statusResult.status
            });
        } catch (error) {
            console.error('Error checking Cognito status:', error);
            this.setState({ 
                isCheckingCognito: false, 
                cognitoAccountStatus: 'error'
            });
        }
    }

    // Filter users based on search
    handleSearch = (event) => {
        const searchTerm = event.target.value.toLowerCase();
        const filtered = this.state.userList.filter(user =>
            user.user_name.toLowerCase().includes(searchTerm) ||
            user.Email.toLowerCase().includes(searchTerm)
        );
        this.setState({ searchTerm: event.target.value, filteredUsers: filtered });
    };

    // View mode handlers
    handleAddUser = () => {
        this.setState({ 
            viewMode: 'create',
            data: {
                email: "",
                name: "",
                phone: "",
                user_type: "",
                access: "",
                id: "",
                primary_company_id: "",
                company_type: ""
            },
            errors: {},
            userCompanies: [],
            allUserCompanies: [],
            cognitoAccountStatus: null
        });
    };

    handleEditUser = async (user) => {
        //console.log("Editing user:", user);
        
        // Get the user's actual type from the User_type field
        let userType = "";
        let companyType = "";
        
        if (user.User_type === "dealer") {
            userType = "dealer";
            companyType = "dealership";
        } else if (user.User_type === "installer" || user.User_type === "specialist") {
            userType = "installer";
            companyType = "specialist";
        } else {
            // Fallback: determine from primary company if User_type is unclear
            const company = this.state.availableCompanies.find(c => 
                (c.dealer_id && c.dealer_id === user.Company_id) || 
                (c.Installer_id && c.Installer_id === user.Company_id)
            );
            if (company) {
                companyType = company.company_type;
                userType = company.company_type === "dealership" ? "dealer" : "installer";
            }
        }
        
        //console.log("User type determined:", userType, "Company type:", companyType);
        
        const userData = {
            email: user.Email,
            name: user.user_name,
            phone: user.user_phone_number,
            user_type: userType,
            access: user.access || "",
            id: user.User_id,
            primary_company_id: user.Company_id,
            company_type: companyType
        };

        //console.log("Setting user data:", userData);

        this.setState({ 
            viewMode: 'edit',
            data: userData,
            errors: {},
            cognitoAccountStatus: null,
            userCompanies: [],
            allUserCompanies: []
        });

        // Load user's additional company associations
        //console.log("Loading user companies for user ID:", user.User_id);
        await this.loadUserCompanies(user.User_id);
        
        // Check Cognito status
        if (user.Email) {
            await this.checkCognitoStatus(user.Email);
        }
    };

    handleBackToList = () => {
        this.setState({ viewMode: 'list' });
    };

    // Get all companies this user has access to (including primary)
    getUserAccessibleCompanies = () => {
        if (!this.state.data.company_type) return [];
        
        // Use the stored allUserCompanies if available
        const allCompanies = this.state.allUserCompanies || [];
        
        // Filter to user's company type
        const userTypeCompanies = allCompanies.filter(uc => 
            uc.company_type === this.state.data.company_type
        );
        
        //console.log("User accessible companies:", userTypeCompanies);
        return userTypeCompanies;
    };

    // Handle primary company selection
    handlePrimaryCompanyChange = (event) => {
        const companyId = event.target.value;
        
        const data = { ...this.state.data };
        data.primary_company_id = companyId;
        
        this.setState({ data });
    };

    // Handle company selection for new users
    handleNewUserCompanyChange = (event) => {
        const companyId = event.target.value;
        const company = this.state.availableCompanies.find(c => 
            (c.dealer_id && c.dealer_id === companyId) || 
            (c.Installer_id && c.Installer_id === companyId)
        );
        
        if (company) {
            const data = { ...this.state.data };
            data.primary_company_id = companyId;
            data.company_type = company.company_type;
            this.setState({ data });
        }
    };

    // Handle additional company associations
    handleAddCompanyAssociation = (companyId) => {
        const company = this.state.availableCompanies.find(c => 
            (c.dealer_id && c.dealer_id === companyId) || 
            (c.Installer_id && c.Installer_id === companyId)
        );

        if (company && company.company_type === this.state.data.company_type) {
            // Check if already associated
            const alreadyAssociated = this.state.userCompanies.some(uc => 
                uc.company_id === companyId
            );
            
            if (!alreadyAssociated && companyId !== this.state.data.primary_company_id) {
                const newAssociation = {
                    company_id: companyId,
                    company_name: company.dealer_name || company.Installer_Name,
                    company_type: company.company_type
                };
                
                this.setState({
                    userCompanies: [...this.state.userCompanies, newAssociation]
                });
            }
        }
    };

    handleRemoveCompanyAssociation = (companyId) => {
        const updatedCompanies = this.state.userCompanies.filter(uc => 
            uc.company_id !== companyId
        );
        this.setState({ userCompanies: updatedCompanies });
    };

    // Get available companies for additional associations
    getAvailableAdditionalCompanies = () => {
        const sameTypeCompanies = this.state.availableCompanies.filter(c => 
            c.company_type === this.state.data.company_type
        );
        
        // Get all companies user currently has access to
        const userAccessibleCompanies = this.getUserAccessibleCompanies();
        const accessibleCompanyIds = userAccessibleCompanies.map(c => c.company_id);
        
        // Return companies not already accessible to user
        return sameTypeCompanies.filter(c => {
            const companyId = c.dealer_id || c.Installer_id;
            return !accessibleCompanyIds.includes(companyId);
        });
    };

    // Form validation
    userSchema = {
        email: JOI.string().email().required().label('email'),
        name: JOI.string().required().label('name'),
        phone: JOI.string().regex(/^[0-9]{10}$/).required().label('phone'),
        primary_company_id: JOI.string().required().label('primary company'),
        company_type: JOI.string().required().label('company type'),
        // access only required for dealership users
        access: JOI.when('company_type', {
            is: 'dealership',
            then: JOI.string().required(),
            otherwise: JOI.optional().allow('')
        }).label('access level'),
        user_type: JOI.optional(),
        id: JOI.optional()
    };

    validate = () => {
        const options = { abortEarly: false };
        const { error } = JOI.validate(this.state.data, this.userSchema, options);
        
        if (!error) return true;
    
        const errors = {};
        for (let item of error.details) errors[item.path[0]] = item.message;
        this.setState({ errors });
        return false;
    };

    validateProperty = ({ name, value }) => {
        const obj = { [name]: value };
        const schema = { [name]: this.userSchema[name] };
        const { error } = JOI.validate(obj, schema);
        return error ? error.details[0].message : null;
    };
      
    handleDataChange = ({ currentTarget: input }) => {
        const errors = { ...this.state.errors };
        const errorMessage = this.validateProperty(input);
        if (errorMessage) errors[input.name] = errorMessage;
        else delete errors[input.name];
    
        const data = { ...this.state.data };
        data[input.name] = input.value;
    
        this.setState({ data, errors });
    };

    // Save user (create or update)
    handleSave = async () => {
        if (!this.validate()) return;

        try {
            const userData = {
                email: this.state.data.email,
                userName: this.state.data.name,
                userPhoneNumber: this.state.data.phone,
                companyId: this.state.data.primary_company_id,
                companyType: this.state.data.company_type,
                active: 1
            };

            // Add access for dealership users
            if (this.state.data.company_type === 'dealership') {
                userData.access = this.state.data.access;
            }

            let userId;
            if (this.state.viewMode === 'create') {
                const { data: result } = await API.post("/users", userData);
                userId = result.userId;
                alert("User created successfully!");
            } else {
                userData.userId = this.state.data.id;
                await API.put("/users/" + this.state.data.id, userData);
                userId = this.state.data.id;
                alert("User updated successfully!");
            }

            // Save additional company associations
            await this.saveUserCompanyAssociations(userId);

            // Reload users and return to list
            await this.loadUsers();
            this.setState({ viewMode: 'list' });

        } catch (error) {
            console.error('Error saving user:', error);
            alert("Error saving user: " + (error.response?.data?.message || error.message));
        }
    };

    // Save user-company associations
    saveUserCompanyAssociations = async (userId) => {
        try {
            // Send all company associations (primary + additional)
            const associations = [
                {
                    company_id: this.state.data.primary_company_id,
                    company_type: this.state.data.company_type
                },
                ...this.state.userCompanies.map(uc => ({
                    company_id: uc.company_id,
                    company_type: uc.company_type
                }))
            ];

            await API.post("/useraccess/" + userId, { associations });
        } catch (error) {
            console.error('Error saving company associations:', error);
        }
    };

    // Password reset
    handlePasswordReset = async () => {
        try {
            const resetData = { 
                email: this.state.data.email
            };
            await API.post("/auth/request-password-reset", resetData);
            alert("Password reset email sent successfully!");
            await this.checkCognitoStatus(this.state.data.email);
        } catch (error) {
            alert("Error sending password reset: " + (error.response?.data?.message || error.message));
        }
    };

    // Get Cognito status display
    getCognitoStatusDisplay = () => {
        if (this.state.isCheckingCognito) {
            return { text: "Checking account status...", color: "#6c757d", bgColor: "#f8f9fa" };
        }
        
        switch (this.state.cognitoAccountStatus) {
            case 'active':
                return { text: "✅ Cognito Account Active", color: "#006600", bgColor: "#e6ffe6" };
            case 'pending':
                return { text: "⏳ Cognito Account Pending", color: "#cc6600", bgColor: "#fff3e0" };
            case 'disabled':
                return { text: "⚠️ Cognito Account Disabled", color: "#cc0000", bgColor: "#ffe6e6" };
            case 'not_found':
                return { text: "❌ No Cognito Account Found", color: "#cc0000", bgColor: "#ffe6e6" };
            case 'error':
                return { text: "❌ Error checking Cognito status", color: "#cc0000", bgColor: "#ffe6e6" };
            default:
                return null;
        }
    };

    // Render methods
    renderUserList = () => {
        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4>User Management</h4>
                    <button 
                        className="btn btn-primary"
                        onClick={this.handleAddUser}
                    >
                        <i className="fa fa-plus"></i> Add New User
                    </button>
                </div>

                {/* Search */}
                <div className="mb-3">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search users by name or email..."
                        value={this.state.searchTerm}
                        onChange={this.handleSearch}
                        style={{ maxWidth: "400px" }}
                    />
                </div>

                {/* User Table */}
                <div className="table-responsive">
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Access Level</th>
                                <th>Primary Company</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.filteredUsers.map(user => {
                                // Determine user type and find the correct company
                                let company = null;
                                
                                if (user.User_type === "dealer") {
                                    company = this.state.dealerList.find(d => d.dealer_id === user.Company_id);
                                } else if (user.User_type === "installer" || user.User_type === "specialist") {
                                    company = this.state.specialistList.find(s => s.Installer_id === user.Company_id);
                                } else {
                                    // Fallback: try both if User_type is unclear
                                    company = this.state.availableCompanies.find(c => 
                                        (c.dealer_id && c.dealer_id === user.Company_id) || 
                                        (c.Installer_id && c.Installer_id === user.Company_id)
                                    );
                                }
                                
                                const companyName = company ? (company.dealer_name || company.Installer_Name) : 'Unknown Company';
                                
                                return (
                                    <tr key={user.User_id}>
                                        <td>{user.user_name}</td>
                                        <td>{user.Email}</td>
                                        <td>{user.user_phone_number}</td>
                                        <td>{user.access || 'N/A'}</td>
                                        <td>{companyName}</td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => this.handleEditUser(user)}
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    renderUserForm = () => {
        const isEdit = this.state.viewMode === 'edit';
        const availableAdditionalCompanies = this.getAvailableAdditionalCompanies();

        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4>{isEdit ? 'Edit User' : 'Create New User'}</h4>
                    <button 
                        className="btn btn-secondary"
                        onClick={this.handleBackToList}
                    >
                        <i className="fa fa-arrow-left"></i> Back to List
                    </button>
                </div>

                <div className="row">
                    <div className="col-md-8">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">User Information</h5>
                                
                                {/* Cognito Status */}
                                {isEdit && this.getCognitoStatusDisplay() && (
                                    <div className="alert" style={{ 
                                        backgroundColor: this.getCognitoStatusDisplay().bgColor,
                                        color: this.getCognitoStatusDisplay().color,
                                        borderColor: this.getCognitoStatusDisplay().color
                                    }}>
                                        {this.getCognitoStatusDisplay().text}
                                        {!this.state.isCheckingCognito && (
                                            <button 
                                                className="btn btn-sm btn-outline-secondary ml-2"
                                                onClick={() => this.checkCognitoStatus(this.state.data.email)}
                                            >
                                                Refresh
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* User Type Display for Edit Mode */}
                                {isEdit && (
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="alert alert-info">
                                                <strong>User Type:</strong> {this.state.data.user_type === 'dealer' ? 'Dealer User' : 'Specialist User'} 
                                                <span className="ml-2">
                                                    (Managing {this.state.data.company_type === 'dealership' ? 'Dealership' : 'Specialist'} companies)
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Form Fields */}
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Full Name *</label>
                                            <input 
                                                type="text"
                                                name="name"
                                                className="form-control"
                                                value={this.state.data.name}
                                                onChange={this.handleDataChange}
                                            />
                                            {this.state.errors.name && 
                                                <div className="text-danger">{this.state.errors.name}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Email *</label>
                                            <input 
                                                type="email"
                                                name="email"
                                                className="form-control"
                                                value={this.state.data.email}
                                                onChange={this.handleDataChange}
                                            />
                                            {this.state.errors.email && 
                                                <div className="text-danger">{this.state.errors.email}</div>}
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Phone (10 digits) *</label>
                                            <input 
                                                type="text"
                                                name="phone"
                                                className="form-control"
                                                value={this.state.data.phone}
                                                onChange={this.handleDataChange}
                                                placeholder="1234567890"
                                            />
                                            {this.state.errors.phone && 
                                                <div className="text-danger">{this.state.errors.phone}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label>Primary Company *</label>
                                            {isEdit ? (
                                                <select 
                                                    name="primary_company_id"
                                                    className="form-control"
                                                    value={String(this.state.data.primary_company_id)}
                                                    onChange={this.handlePrimaryCompanyChange}
                                                >
                                                    <option value="">Select Primary Company</option>
                                                    {this.getUserAccessibleCompanies().map(company => {
                                                        const companyId = company.company_id;
                                                        const companyName = company.company_name;
                                                        return (
                                                            <option key={companyId} value={companyId}>
                                                                {companyName}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            ) : (
                                                <select 
                                                    name="primary_company_id"
                                                    className="form-control"
                                                    value={String(this.state.data.primary_company_id)}
                                                    onChange={this.handleNewUserCompanyChange}
                                                >
                                                    <option value="">Select Primary Company</option>
                                                    <optgroup label="Dealerships">
                                                        {this.state.dealerList.map(dealer => (
                                                            <option key={dealer.dealer_id} value={dealer.dealer_id}>
                                                                {dealer.dealer_name}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                    <optgroup label="Specialists">
                                                        {this.state.specialistList.map(specialist => (
                                                            <option key={specialist.Installer_id} value={specialist.Installer_id}>
                                                                {specialist.Installer_Name}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                </select>
                                            )}
                                            {this.state.errors.primary_company_id && 
                                                <div className="text-danger">{this.state.errors.primary_company_id}</div>}
                                            {isEdit && (
                                                <small className="text-muted">
                                                    Choose from companies this user has access to
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Access Level - Only for Dealership Users */}
                                {this.state.data.company_type === 'dealership' && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group">
                                                <label>Access Level *</label>
                                                <select 
                                                    name="access"
                                                    className="form-control"
                                                    value={this.state.data.access}
                                                    onChange={this.handleDataChange}
                                                >
                                                    <option value="">Select Access Level</option>
                                                    {this.state.dealerAccessLevels.map(level => (
                                                        <option key={level.value} value={level.value}>
                                                            {level.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {this.state.errors.access && 
                                                    <div className="text-danger">{this.state.errors.access}</div>}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="form-group">
                                    <button 
                                        className="btn btn-success mr-2"
                                        onClick={this.handleSave}
                                    >
                                        {isEdit ? 'Update User' : 'Create User'}
                                    </button>
                                    
                                    {isEdit && (
                                        <button 
                                            className="btn btn-warning"
                                            onClick={this.handlePasswordReset}
                                            disabled={!this.state.data.email}
                                        >
                                            Reset Password
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Company Associations Panel */}
                    <div className="col-md-4">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Company Access</h5>
                                
                                {/* Company Access Management */}
                                <div>
                                    <strong>All Company Access:</strong>
                                    <div className="mt-2">
                                        {this.getUserAccessibleCompanies().length === 0 ? (
                                            <small className="text-muted">No company access configured</small>
                                        ) : (
                                            this.getUserAccessibleCompanies().map((company, index) => {
                                                const companyId = company.company_id;
                                                const companyName = company.company_name;
                                                const isPrimary = companyId === this.state.data.primary_company_id;
                                                
                                                return (
                                                    <div key={companyId || index} className="d-flex justify-content-between align-items-center mt-1 p-2 border rounded">
                                                        <div>
                                                            <span className={`badge ${isPrimary ? 'badge-primary' : 'badge-secondary'}`}>
                                                                {companyName}
                                                            </span>
                                                            {isPrimary && <small className="text-muted ml-2">(Primary)</small>}
                                                        </div>
                                                        {!isPrimary && (
                                                            <button 
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => this.handleRemoveCompanyAssociation(companyId)}
                                                                title="Remove access"
                                                            >
                                                                <i className="fa fa-times"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                {/* Add Additional Company */}
                                {availableAdditionalCompanies.length > 0 && (
                                    <div className="mt-3">
                                        <label>Add Company Access:</label>
                                        <select 
                                            className="form-control form-control-sm"
                                            onChange={(e) => {
                                                if (e.target.value) {
                                                    this.handleAddCompanyAssociation(e.target.value);
                                                    e.target.value = "";
                                                }
                                            }}
                                        >
                                            <option value="">Select company to add...</option>
                                            {availableAdditionalCompanies.map(company => {
                                                const companyId = company.dealer_id || company.Installer_id;
                                                const companyName = company.dealer_name || company.Installer_Name;
                                                return (
                                                    <option key={companyId} value={companyId}>
                                                        {companyName}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        return (
            <div className="container-fluid">
                {this.state.viewMode === 'list' && this.renderUserList()}
                {(this.state.viewMode === 'create' || this.state.viewMode === 'edit') && this.renderUserForm()}
            </div>
        );
    }
}